

import * as L from "leaflet";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LayersControl,
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMapEvents,
  LayerGroup,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { ReactLegendMap } from "features/dashboard/Project/ReactLegendMap";
import { useMap } from "react-leaflet";

const pointIcon = L.icon({
  iconUrl: require("./marker4.jpg"),
  iconSize: [24, 35],
  iconAnchor: [12, 35],
  popupAnchor: [0, -35],
});

const polygonCenterIcon = L.icon({
  iconUrl:
    "https://www.iconarchive.com/download/i113269/icons-land/flat-vector-map-marker/Marker-Pearl-HeadPin-Red.1024.png",
  iconSize: [0, 0],
  iconAnchor: [8, 8],
  popupAnchor: [0, -8],
});

const getColor = (ACS) => {
  if (ACS === null || typeof ACS === "object") return "blue";

  if (ACS < 50) return "red";
  if (ACS >= 50 && ACS <= 60) return "yellow";
  return "green";
};

const ZoomTracker = ({ onZoomChange }) => {
  useMapEvents({
    zoomend: (e) => onZoomChange(e.target.getZoom()),
  });
  return null;
};

const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [40, 40],
        maxZoom: 18,
        animate: true,
      });
    }
  }, [bounds, map]);

  return null;
};


const FarmerMap = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [geoJson, setGeoJson] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(5);

  useEffect(() => {
    if (props.showGeojson) {
      setGeoJson(props.showGeojson);
    }
  }, [props.showGeojson]);

  useEffect(() => {
    if (geoJson) {
      try {
        const leafletBounds = L.geoJSON(geoJson).getBounds();
        setBounds(leafletBounds);
      } catch (err) {
        console.warn("Failed to compute bounds:", err);
      }
    }
  }, [geoJson]);
  const polygonFeatures = useMemo(
    () =>
      geoJson?.features?.filter(
        (feature) =>
          feature?.geometry &&
          ["Polygon", "MultiPolygon"].includes(feature.geometry.type) &&
          feature.properties
      ) || [],
    [geoJson]
  );

  const pointFeatures = useMemo(
    () =>
      geoJson?.features?.filter(
        (feature) =>
          feature?.geometry?.type === "Point" &&
          Array.isArray(feature.geometry.coordinates) &&
          feature.properties
      ) || [],
    [geoJson]
  );
 

  const combinedMarkers = useMemo(() => {
    const uniquePointMap = new Map();
   
    pointFeatures.forEach((feature) => {
      const plotId = feature.properties.plot_id;
      if (plotId && !uniquePointMap.has(plotId)) {
        uniquePointMap.set(plotId, feature);
      }
    });

    const pointMarkers = Array.from(uniquePointMap.values()).map(
      (feature, idx) => {
        const [lng, lat] = feature.geometry.coordinates;
        return {
          key: `point-${feature.properties.plot_id ?? `idx-${idx}`}`,
          position: [lat, lng],
          icon: pointIcon,
          plotId: feature.properties.plot_id,
          feature,
        };
      }
    );

    const existingPlotIds = new Set(pointMarkers.map((m) => m.plotId));

    const uniquePolygonMap = new Map();
    polygonFeatures.forEach((feature) => {
      const plotId = feature.properties.plot_id;
      if (
        plotId &&
        !existingPlotIds.has(plotId) &&
        !uniquePolygonMap.has(plotId)
      ) {
        uniquePolygonMap.set(plotId, feature);
      }
    });
    const polygonCenterMarkers = Array.from(uniquePolygonMap.values())
      .map((feature, idx) => {
        const bounds = L.geoJSON(feature).getBounds();
        if (!bounds.isValid()) return null;
        const latlng = bounds.getCenter();
        return {
          key: `poly-center-${feature.properties.plot_id ?? `idx-${idx}`}`,
          position: [latlng.lat, latlng.lng],
          icon: polygonCenterIcon,
          plotId: feature.properties.plot_id,
          feature,
        };
      })
      .filter(Boolean);
  
    return [...pointMarkers, ...polygonCenterMarkers];
  }, [pointFeatures, polygonFeatures]);


 
  return (
    <div style={{ position: "relative", height: "100%", width: "100%",zIndex:0 }}>
      {/* <MapContainer
        center={bounds ? undefined : [22, 80]}
        zoom={bounds ? undefined : 5}
        bounds={bounds}

        style={{ height: "100%", width: "100%" }}
        whenCreated={(map) => setZoom(map.getZoom())}
      > */}

          <MapContainer
         center={bounds ? undefined : [22, 80]}
         zoom={bounds ? undefined : 5}
         bounds={bounds}
        style={{ height: "100%", width: "100%" }}
         whenCreated={(map) => setZoom(map.getZoom())}
         >

          {bounds && <FitBounds bounds={bounds} />}

        <ZoomTracker onZoomChange={setZoom} />

        <LayersControl collapsed={false}>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer url="https://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}"  maxZoom={22}/>
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite With Labels" checked>
            <TileLayer url="https://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}"  maxZoom={22} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Streets">
            <TileLayer url="https://mt0.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}"  maxZoom={22}/>
          </LayersControl.BaseLayer>

          <LayersControl.Overlay name="Plots" checked>
            <LayerGroup>
              {polygonFeatures.map((feature, idx) => (
                <GeoJSON
                  key={`polygon-${idx}`}
                  data={feature}
                  style={{
                    color: getColor(feature?.properties?.ACS ?? null),
                    weight: 2,
                    fillOpacity: 0.5,
                  }}
                  onEachFeature={(feature, layer) => {
                    const Wrapperdiv = document.createElement("div");
                    Wrapperdiv.className = "farmer-info-wrapper lineHeight";
                    Wrapperdiv.innerHTML = `<div className="farmerName">
                      <div><b>Farmer Name</b>: ${
                        feature.properties.farmer_name
                      }</div>
                      <div><b>Mobile Number</b>: ${
                        feature.properties.phone_number
                      }</div>
                      <div><b>State</b>: ${feature.properties.state_name}</div>
                      <div><b>District</b>: ${
                        feature.properties.district_name
                      }</div>
                      <div><b>Land Mark</b>: ${
                        feature.properties.land_mark
                      }</div>
                      <div><b>Plot Id</b>: ${feature.properties.plot_id}</div>
                      <div><b>ACS</b>: ${feature.properties.ACS ?? 0}</div>
                    </div>`;
                    const btn = document.createElement("button");
                    btn.innerHTML = "View Details";
                    btn.style.cssText = `
                      color: white;
                      background-color: green;
                      padding: 7px;
                      border: none;
                      width: 100%;
                      margin-top: 10px;
                    `;
                    btn.onclick = () =>
                      navigate(
                        `/onboarding/farmers/${feature.properties.farmer_id}`
                      );
                    Wrapperdiv.appendChild(btn);
                    layer.bindPopup(Wrapperdiv);
                  }}
                />
              ))}
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                removeOutsideVisibleBounds={false}     
              >
                {combinedMarkers.map(({ key, position, icon, feature }) => (
                  <Marker key={key} position={position} icon={icon}>
                    <Popup>
                      <div className="farmer-info-wrapper2">
                        <div className="mt-3">
                          <b>Farmer Name</b>: {feature.properties.farmer_name}
                        </div>
                        <div className="mt-3">
                          <b>Phone</b>: {feature.properties.phone_number}
                        </div>
                        <div className="mt-3">
                          <b>State</b>: {feature.properties.state_name}
                        </div>
                        <div className="mt-3">
                          <b>District</b>: {feature.properties.district_name}
                        </div>
                        <div className="mt-3">
                          <b>Land Mark</b>: {feature.properties.land_mark}
                        </div>
                        <div className="mt-3">
                          <b>Plot Id</b>: {feature.properties.plot_id}
                        </div>
                        <div className="mt-3">
                          <b>ACS</b>: {feature.properties.ACS ?? 0}
                        </div>
                        <button
                          style={{
                            color: "white",
                            backgroundColor: "green",
                            padding: "7px",
                            border: "none",
                            width: "100%",
                            marginTop: "10px",
                          }}
                          onClick={() =>
                            navigate(
                              `/onboarding/farmers/${feature.properties.farmer_id}`
                            )
                          }
                        >
                          View Details
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
        <ReactLegendMap />
      </MapContainer>
    </div>
  );
};

export default FarmerMap;

